import { HTTP_METHODS, invokeApi } from "../../utils/http-service";
import { CF_APPROVAL_HOSTNAME, INTRANETHOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";
import { REST_URLS as INTRANET_URLS } from "../../../common-utilities/utils/end-points/intranet";
import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { getV2HeaderConfig, getOrderedV2Steps } from "./config";
import { toast } from "react-toastify";
import MuiTable from "../../../common-utilities/core/mui-table";
import { Box, Button, Chip, CircularProgress, IconButton, Stack, Tooltip } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import CfV2FilterModal from "../cf-v2-forms-status-tabs/components/filter-modal";
import { exportToExcel } from "../../../common-utilities/utils";
import { getOfflineData } from "../../utils/offline-services";
import moment from "moment";
import "./index.scss";
import { DIRECT_DISPLAY_STATUSES } from "../cf-v2-forms-status-tabs/v2-config";
import { getCfStatusLabel } from "../../utils/constants";

const LIMIT = 10;

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

const getStepTimestamp = (data, stepNumber) => {
  const step = [...(data?.stepHistory || [])]
    .reverse()
    .find((h) => h.stepNumber === stepNumber && h.action === "APPROVED");
  return step?.timestamp ? moment(step.timestamp).format("YYYY-MM-DD HH:mm:ss") : "N/A";
};

// "Lead Creation" always reflects the record's own creation date — distinct from
// "Step 1 (CF Upload)" above, which only fills in once Step 1 is actually approved.
const getCreationTimestamp = (data) => {
  const created = (data?.stepHistory || []).find((h) => h.action === "CREATED");
  const ts = created?.timestamp || data?.createdAt;
  return ts ? moment(ts).format("YYYY-MM-DD HH:mm:ss") : "N/A";
};

const showDownloadFlatSheets = (role) => {
  const upperRole = String(role || "").toUpperCase();
  return ["BUSINESS_FINANCE", "CF_READ_ONLY"].includes(upperRole);
};
export const CfV2Report = () => {
  const [formsList, setFormsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [userMap, setUserMap] = useState({});
  const stepMasterList = useSelector((state) => state.cfApprovalV2.stepMaster) || [];
  const [downloading, setDownloading] = useState(false);
  const [downloadingFlatSheets, setDownloadingFlatSheets] = useState(false);

  const userDetails = getOfflineData("user") || {};
  const role = userDetails?.role?.toUpperCase();
  const isBusinessFinance = showDownloadFlatSheets(role);

  const loadUsers = () => {
    invokeApi(HTTP_METHODS.GET, `${INTRANETHOSTNAME}${INTRANET_URLS.FILTER_USER}`, null, {
      appLink: "cf-approval-v2",
      page: 1,
      limit: 100,
    }).then((res) => {
      if (Array.isArray(res?.results)) {
        const map = {};
        res.results.forEach((user) => { map[user.id] = user.name; });
        setUserMap(map);
      }
    });
  };

  useEffect(() => { loadUsers(); }, []);

  const loadData = useCallback(() => {
    setLoading(true);
    const queryParams = { page, limit: LIMIT, ...appliedFilters };
    invokeApi(HTTP_METHODS.GET, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}`, null, queryParams)
      .then((response) => {
        if (response?.result) {
          setTotalCount(response?.total || response?.result?.length || 0);
          setFormsList(response?.result || []);
        } else {
          setFormsList([]);
        }
      })
      .catch((err) => {
        console.error("Error loading v2 forms:", err);
        toast.error("Failed to load forms");
      })
      .finally(() => setLoading(false));
  }, [page, appliedFilters]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters);
    setPage(1);
    setShowFilterModal(false);
  };

  const removeFilter = (key) => {
    setAppliedFilters((prev) => {
      const updated = { ...prev };
      if (key === "date") {
        delete updated.dateFrom;
        delete updated.dateTo;
      } else {
        delete updated[key];
      }
      return updated;
    });
    setPage(1);
  };

  const handleDownload = () => {
    setDownloading(true);
    invokeApi(
      HTTP_METHODS.GET,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}`,
      null,
      { page: 1, limit: totalCount || 1000, ...appliedFilters },
    )
      .then((response) => {
        const steps = getOrderedV2Steps(stepMasterList);
        const rows = (response?.result || []).map((item) => {
          const row = {
            "CF Id": item?.id || "",
            "Site Name": item?.siteName || "",
            Region: item?.address?.region || "",
            Circle: item?.address?.circle || "",
            City: item?.address?.city || "",
            "Current Step": item?.currentStepDetails ? `Step ${item.currentStepDetails.stepNumber} - ${item.currentStepDetails.name}` : "N/A",
            "Status": getCfStatusLabel(DIRECT_DISPLAY_STATUSES.includes(item?.status) ? item.status : item.currentStepDetails.cfStatus),
            "Store Code": `${item.storeCode || 'x'}-${item.sequenceCode || 'x'}-${item.satelliteCode || 'x'}`,
            "Created By": userMap[item?.createdBy] || "N/A",
            Relocated: item?.relocated ? "Yes" : "No",
            "Part of AOP list’": item?.partOfAOP ? "Yes" : "No",
          };
          steps.forEach((s) => {
            row[`Step ${s.stepNumber} (${s.name})`] = s.isCreationEvent
              ? getCreationTimestamp(item)
              : getStepTimestamp(item, s.stepNumber);
          });
          return row;
        });
        exportToExcel(rows, "CF_V2_Dashboard");
      })
      .catch(() => toast.error("Failed to download"))
      .finally(() => setDownloading(false));
  };

  const handleDownloadFlatSheets = () => {
    setDownloadingFlatSheets(true);
    invokeApi(HTTP_METHODS.GET, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_FINANCIAL_ALL}`)
      .then((response) => {
        const rows = (Array.isArray(response) ? response : []).map((item) => ({
          "CF Form ID": item.cfFormId,
          "Site Name": item.siteName || "",
          ...item.data,
        }));
        exportToExcel(rows, "CF_V2_Flat_Sheets");
      })
      .catch(() => toast.error("Failed to download flat sheets"))
      .finally(() => setDownloadingFlatSheets(false));
  };

  const filterChips = [];
  if (appliedFilters.siteName) filterChips.push({ key: "siteName", label: `Site: ${appliedFilters.siteName}` });
  if (appliedFilters.id) filterChips.push({ key: "id", label: `ID: ${appliedFilters.id}` });
  if (appliedFilters.dateFrom || appliedFilters.dateTo) {
    filterChips.push({
      key: "date",
      label: `${formatDate(appliedFilters.dateFrom)} – ${formatDate(appliedFilters.dateTo)}`,
    });
  }
  if (appliedFilters.createdBy) filterChips.push({ key: "createdBy", label: `Created By: ${userMap[appliedFilters.createdBy] || appliedFilters.createdBy}` });
  if (appliedFilters.region) filterChips.push({ key: "region", label: `Region: ${appliedFilters.region}` });
  if (appliedFilters.circle) filterChips.push({ key: "circle", label: `Circle: ${appliedFilters.circle}` });

  if (loading && formsList.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        justifyContent="flex-end"
        flexWrap="wrap"
        sx={{ px: 1, py: 0.75 }}
      >
        {filterChips.map((chip) => (
          <Chip
            key={chip.key}
            label={chip.label}
            size="small"
            onDelete={() => removeFilter(chip.key)}
            sx={{ fontSize: "0.7rem" }}
          />
        ))}
        <Tooltip title="Filters">
          <IconButton
            onClick={() => setShowFilterModal(true)}
            size="small"
            color={filterChips.length > 0 ? "primary" : "default"}
          >
            <FilterListIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download Excel">
          <span>
            <IconButton
              onClick={handleDownload}
              size="small"
              disabled={downloading || formsList.length === 0}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {isBusinessFinance && (
          <Button
            onClick={handleDownloadFlatSheets}
            size="small"
            variant="outlined"
            disabled={downloadingFlatSheets}
            startIcon={downloadingFlatSheets ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
          >
            Download Flat Sheet
          </Button>
        )}
      </Stack>

      <MuiTable
        columnsList={getV2HeaderConfig(userMap || {}, stepMasterList)}
        dataList={formsList}
        filters={{ page, limit: LIMIT }}
        pageCount={Math.ceil(totalCount / LIMIT) || 1}
        onChange={(newPage) => setPage(newPage)}
      />

      {showFilterModal && (
        <CfV2FilterModal
          onClose={() => setShowFilterModal(false)}
          onApply={handleApplyFilters}
          initialFilters={appliedFilters}
          userMap={userMap}
        />
      )}
    </div>
  );
};
